interface TeamScore {
  name: string;
  logo?: string;
  score?: number;
  color?: string;
}

interface Match {
  id: string;
  status: "live" | "finished" | "upcoming";
  competition: string;
  home: TeamScore;
  away: TeamScore;
  date?: string;
  time?: string;
  venue?: string;
  minute?: number;
}

interface StandingRow {
  position: number;
  team: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  gf: number;
  ga: number;
  points: number;
}

interface SportsData {
  sport: string;
  display_type: "match" | "matches" | "standings" | "player_stats";
  title: string;
  matches?: Match[];
  standings?: StandingRow[];
  highlight_team?: string;
}

interface Props {
  data: SportsData;
}

function StatusBadge({ status, minute }: { status: Match["status"]; minute?: number }) {
  if (status === "live") {
    return (
      <span className="sports-badge live">
        <span className="sports-badge-dot" />
        {minute ? `${minute}'` : "LIVE"}
      </span>
    );
  }
  if (status === "finished") return <span className="sports-badge finished">Ferdig</span>;
  return <span className="sports-badge upcoming">Kommende</span>;
}

function MatchCard({ match, highlightTeam }: { match: Match; highlightTeam?: string }) {
  const isHighlightHome = highlightTeam && match.home.name.toLowerCase().includes(highlightTeam.toLowerCase());
  const isHighlightAway = highlightTeam && match.away.name.toLowerCase().includes(highlightTeam.toLowerCase());

  return (
    <div className="sports-match">
      <div className="sports-match-header">
        <span className="sports-competition">{match.competition}</span>
        <StatusBadge status={match.status} minute={match.minute} />
      </div>
      <div className="sports-match-teams">
        <div className={`sports-team${isHighlightHome ? " highlight" : ""}`}>
          {match.home.logo && <img src={match.home.logo} alt="" className="sports-team-logo" />}
          <span className="sports-team-name">{match.home.name}</span>
        </div>
        <div className="sports-score">
          {match.status !== "upcoming" ? (
            <span className="sports-score-value">
              {match.home.score ?? 0} — {match.away.score ?? 0}
            </span>
          ) : (
            <span className="sports-score-time">{match.time || "—"}</span>
          )}
        </div>
        <div className={`sports-team away${isHighlightAway ? " highlight" : ""}`}>
          {match.away.logo && <img src={match.away.logo} alt="" className="sports-team-logo" />}
          <span className="sports-team-name">{match.away.name}</span>
        </div>
      </div>
      {match.venue && <div className="sports-venue">{match.venue}</div>}
    </div>
  );
}

export default function SportsCard({ data }: Props) {
  return (
    <div className="tool-sports">
      <div className="sports-header">
        <span className="sports-title">{data.title}</span>
      </div>

      {data.matches && data.matches.map(m => (
        <MatchCard key={m.id} match={m} highlightTeam={data.highlight_team} />
      ))}

      {data.standings && (
        <div className="sports-standings">
          <table className="sports-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Lag</th>
                <th>K</th>
                <th>V</th>
                <th>U</th>
                <th>T</th>
                <th>M</th>
                <th>P</th>
              </tr>
            </thead>
            <tbody>
              {data.standings.map(row => (
                <tr
                  key={row.position}
                  className={
                    data.highlight_team && row.team.toLowerCase().includes(data.highlight_team.toLowerCase())
                      ? "highlight"
                      : ""
                  }
                >
                  <td>{row.position}</td>
                  <td className="sports-table-team">{row.team}</td>
                  <td>{row.played}</td>
                  <td>{row.won}</td>
                  <td>{row.drawn}</td>
                  <td>{row.lost}</td>
                  <td>{row.gf}–{row.ga}</td>
                  <td className="sports-table-points">{row.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
